import localforage from 'localforage';
// eslint-disable-next-line import/no-cycle
import { httpX } from 'src/plugins/http';
import { update } from 'src/permission';
import { resetCache } from '@/helpers/api';
import Ably from '@/services/ably';
import { showSessionEndedDialog } from '@/helpers/authSession';
import getDeviceId from '@/helpers/device';

const V3_SESSION_KEYS = ['auth_token', 'auth_user', 'v2_auth_token'];

function clearV3Session() {
  try {
    V3_SESSION_KEYS.forEach(key => localStorage.removeItem(key));
    return true;
  } catch (e) {
    return false;
  }
}

export const attemptLogin = ({ dispatch }, payload) => (
  httpX.post('/auth', { ...payload, deviceId: getDeviceId() })
    .then(({ data }) => {
      const { user, token } = data;

      clearV3Session();
      dispatch('setUser', { ...user, licenseExceeded: data.licenseExceeded ?? false });
      dispatch('setToken', token);
      dispatch('setModule', 'clinical');

      return user;
    })
);

export const redeemSupportAccess = ({ dispatch }, ticket) => (
  httpX.post('/support-access/redeem', { ticket })
    .then(({ data }) => {
      const { user, token } = data;

      clearV3Session();
      dispatch('setUser', user);
      dispatch('setToken', token);
      dispatch('setModule', 'clinical');

      return user;
    })
);

export const resetAuthState = async ({ dispatch }) => {
  await dispatch('stopEventListener');
  dispatch('setUser', null);
  dispatch('setToken', null);
  dispatch('setModule', null);
  resetCache();
};

export const acceptTerms = async ({ commit }) => {
  const { data } = await httpX.post('/customer/terms-acceptance');
  commit('Auth.SET_TERMS', data);
  return data;
};

export const logout = async ({ dispatch }) => {
  clearV3Session();
  await Promise.all([
    httpX.post('/logout'),
    dispatch('resetAuthState'),
  ]);
};

export const setUser = ({ commit, dispatch }, user) => {
  commit('Auth.SET_USER', user);
  update((
    user && user.branch && Array.isArray(user.branch.actions)
      ? user.branch.actions
      : []
  ));
  if (user && user.branch) {
    if (!user.branch.settings) {
      user.branch.settings = {};
    }
    if (!('clinical.blockServiceWithoutAccount' in user.branch.settings)) {
      user.branch.settings['clinical.blockServiceWithoutAccount'] = false;
    }

    dispatch('startEventListener');
  }
  return Promise.resolve(user);
};

export const startEventListener = ({
  state, getters, commit,
}) => {
  Ably.instance(state.user.id).realtime.channels
    .get(getters.realtimeUserGlobalChannel)
    .subscribe('session-update', ({ data }) => {
      if (!data.active) {
        showSessionEndedDialog({
          logoutReason: data.logout?.reason,
          ip: data.newSession?.ip,
          createdAt: data.newSession?.createdAt,
        });
      } else {
        commit('Auth.SET_LICENSE_EXCEEDED', data.licenseExceeded ?? false);
      }
    });
};

export const stopEventListener = ({ state, getters }) => (
  Ably.instance(state.user.id).realtime.channels
    .get(getters.realtimeUserGlobalChannel)
    .unsubscribe('session-unblocked')
);

export const setToken = ({ commit }, token) => {
  commit('Auth.SET_TOKEN', token);
  return Promise.resolve(token);
};

export const setModule = ({ commit }, module) => {
  commit('Auth.SET_MODULE', module);
  return Promise.resolve(module);
};

export const checkUserToken = ({ dispatch, state }) => {
  if (state.token) {
    return Promise.resolve(state.token);
  }

  return Promise.all([
    localforage.getItem('token'),
    localforage.getItem('module').catch(() => 'clinical'),
  ])
    .then(([token, module]) => {
      if (!token) {
        return Promise.reject(new Error('NO_TOKEN'));
      }

      dispatch('setModule', module);

      return dispatch('setToken', token);
    })
    .then(() => dispatch('loadUser'));
};

export const loadUser = ({ dispatch }) => (
  httpX.get('/me')
    .then(({ data }) => dispatch('setUser', data))
    .catch(() => {
      dispatch('logout');
      return Promise.reject(new Error('FAIL_IN_LOAD_USER'));
    })
);
